// src/plugins/builder/templates/new-review-workflow/generator.tsx
// Hook-only: every new review is forwarded to a workflow webhook. Pure plumbing.
import meta from './template.json';
import { manifestFor, header, readme, assemble, safeString } from '../_helpers';
const runtimeHooks = {
    async onReviewCreated(ctx, review) {
        const r = review;
        const cfg = ctx.config;
        if (!cfg.webhookUrl)
            return;
        if ((r.rating ?? 0) < (cfg.minStars ?? 1))
            return;
        if (!ctx.permissions.includes('external:fetch'))
            return;
        await fetch(cfg.webhookUrl, {
            method: 'POST', headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
                id: r.id, platform: r.platform, author: r.author_name,
                rating: r.rating, body: r.body, posted_at: r.posted_at,
                source: 'plugin:builder:new-review-workflow',
            }),
        }).catch(() => { });
    },
};
const generate = (params) => {
    const cfg = params.config;
    const manifest = manifestFor({
        instanceId: params.instanceId, name: params.instanceName,
        description: `Forwards every review (≥${cfg.minStars}★) to your workflow webhook.`,
        category: 'integration', icon: 'Workflow',
        permissions: ['read:reviews', 'external:fetch'],
        entry: 'hook', hooks: { onReviewCreated: true },
        config: meta.configSchema,
        defaultConfig: { webhookUrl: cfg.webhookUrl, minStars: cfg.minStars },
    });
    const hooksTs = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import type { PluginHandlers, HandlerContext } from '../types';
export const handlers: PluginHandlers = {
  async onReviewCreated(ctx: HandlerContext, review: unknown) {
    const r = review as { id: string; rating: number | null; body: string | null; author_name: string | null; platform: string; posted_at: string | null };
    if (!ctx.permissions.includes('external:fetch')) return;
    if ((r.rating ?? 0) < ${Number(cfg.minStars)}) return;
    await fetch('${esc(cfg.webhookUrl)}', { method: 'POST', headers: {'content-type':'application/json'}, body: JSON.stringify({ id: r.id, platform: r.platform, author: r.author_name, rating: r.rating, body: r.body, posted_at: r.posted_at }) }).catch(() => {});
  },
};
export default handlers;
`;
    return assemble({ manifest, pluginTsx: null, hooksTs,
        readmeMd: readme({ name: params.instanceName, templateId: meta.id,
            description: `Sends every review to your workflow webhook.`,
            configSummary: [['webhookUrl', cfg.webhookUrl], ['minStars', String(cfg.minStars)]],
        }),
    });
};
function esc(s) { return safeString(String(s)); }
const template = {
    id: meta.id, name: meta.name, tagline: meta.tagline, icon: meta.icon,
    category: meta.category,
    permissions: meta.permissions,
    permissionSummary: meta.permissionSummary,
    entry: meta.entry,
    hooks: meta.hooks,
    configSchema: meta.configSchema,
    defaultConfig: meta.defaultConfig,
    suggestName: (cfg) => `Workflow trigger (≥${cfg.minStars}★)`,
    describe: (cfg) => `Forward each new review with ${cfg.minStars}★ or more to your workflow.`,
    generate, runtimeHooks,
    clickCount: meta.clickCount,
};
export default template;
